/**
 * Animated neon radial chart for a 64-dim unit-sphere embedding.
 *
 * Positive values span outward from the baseline circle (value = 0).
 * Negative values span inward toward the center.
 * Each of the 64 dimensions receives a distinct neon hue that streams
 * (rotates) continuously — the "colour stream" effect.
 */
export class RadialChart {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly CX: number;
  private readonly CY: number;
  private readonly MAX_R: number;
  private readonly BASE_R: number;

  private animId: number | null = null;
  private hue = 0;

  // Hover tooltip support (used by showDiff)
  private readonly tooltip: HTMLDivElement;
  private lastPts: { x: number; y: number }[] = [];
  private tooltipLabels: string[] = [];

  constructor(
    private readonly container: HTMLElement,
    width = 270,
    height = 188,
  ) {
    // Container needs relative positioning for tooltip overlay
    container.style.position = 'relative';

    this.canvas = document.createElement('canvas');
    this.canvas.width  = width;
    this.canvas.height = height;
    container.appendChild(this.canvas);

    this.ctx    = this.canvas.getContext('2d')!;
    this.CX     = width  / 2;
    this.CY     = height / 2;
    this.MAX_R  = Math.min(this.CX, this.CY) * 0.87;
    this.BASE_R = this.MAX_R * 0.5;

    // Tooltip div — hidden by default
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'sm-radial-tooltip';
    this.tooltip.style.display = 'none';
    container.appendChild(this.tooltip);

    // Hover handlers (only active when tooltipLabels are set)
    this.canvas.addEventListener('mousemove', (e) => this.onHover(e));
    this.canvas.addEventListener('mouseleave', () => { this.tooltip.style.display = 'none'; });

    this.drawIdle();
  }

  /**
   * Start animating for a given 64-dim embedding.
   *
   * @param stretch When true (default) the values are contrast-stretched
   * to fill the full ±1 display range so shape differences are clearly
   * visible even when raw values span only ±0.25.
   */
  show(embed: number[], stretch = true): void {
    this.tooltipLabels = [];  // no hover in plain mode
    if (this.animId !== null) cancelAnimationFrame(this.animId);
    const display = stretch ? RadialChart.contrastStretch(embed) : embed;
    const tick = (): void => {
      this.hue = (this.hue + 0.6) % 360;
      this.draw(display, this.hue);
      this.animId = requestAnimationFrame(tick);
    };
    tick();
  }

  /**
   * Show a signed difference vector with warm/cool coloring.
   * Positive (A > B) → amber. Negative (B > A) → teal.
   *
   * @param diff        Raw per-dimension difference values (64 elements).
   * @param hoverLabels Optional label string per dimension shown on hover.
   *                    Defaults to "A{i}: {diff value}" if omitted.
   */
  showDiff(diff: number[], hoverLabels?: string[]): void {
    if (this.animId !== null) cancelAnimationFrame(this.animId);
    const maxAbs = Math.max(...diff.map(v => Math.abs(v)), 1e-9);
    const scaled = diff.map(v => (v / maxAbs) * 0.92);

    this.tooltipLabels = hoverLabels ??
      diff.map((v, i) => `A${String(i).padStart(2, '0')}: ${v > 0 ? '+' : ''}${v.toFixed(4)}`);

    let phase = 0;
    const tick = (): void => {
      phase = (phase + 0.8) % 360;
      this.drawDiff(scaled, phase);
      this.animId = requestAnimationFrame(tick);
    };
    tick();
  }

  stop(): void {
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
    this.tooltip.style.display = 'none';
  }

  // ── Hover ─────────────────────────────────────────────────────────────────

  private onHover(e: MouseEvent): void {
    if (!this.tooltipLabels.length || !this.lastPts.length) return;

    const rect = this.canvas.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;

    let bestIdx = -1;
    let bestD   = Infinity;
    this.lastPts.forEach((p, i) => {
      const d = (p.x - mx) ** 2 + (p.y - my) ** 2;
      if (d < bestD) { bestD = d; bestIdx = i; }
    });

    if (bestIdx < 0 || bestD > 28 ** 2) {
      this.tooltip.style.display = 'none';
      return;
    }

    this.tooltip.style.display = 'block';
    this.tooltip.textContent   = this.tooltipLabels[bestIdx];

    // Position tooltip near cursor, keep it inside container
    const cw = this.container.clientWidth;
    const ch = this.container.clientHeight;
    let tx = mx + 12;
    let ty = my - 20;
    if (tx + 160 > cw) tx = mx - 170;
    if (ty < 0)        ty = my + 10;
    if (ty + 40 > ch)  ty = ch - 44;
    this.tooltip.style.left = `${tx}px`;
    this.tooltip.style.top  = `${ty}px`;
  }

  // ── Stretch ───────────────────────────────────────────────────────────────

  private static contrastStretch(embed: number[]): number[] {
    const min  = Math.min(...embed);
    const max  = Math.max(...embed);
    const mid  = (max + min) / 2;
    const half = Math.max((max - min) / 2, 1e-9);
    return embed.map(v => ((v - mid) / half) * 0.92);
  }

  // ── Draw: idle ────────────────────────────────────────────────────────────

  private drawIdle(): void {
    const { ctx, CX, CY, BASE_R } = this;
    ctx.fillStyle = '#080d18';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.beginPath();
    ctx.arc(CX, CY, BASE_R, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(151,184,203,.2)';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 6]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(151,184,203,.25)';
    ctx.font = '9px Courier New';
    ctx.textAlign = 'center';
    ctx.fillText('SELECT A SPECIES', CX, CY + 4);
  }

  // ── Draw: normal embed ────────────────────────────────────────────────────

  private draw(embed: number[], hueOffset: number): void {
    const { ctx, CX, CY, MAX_R, BASE_R } = this;
    const N = 64;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = '#080d18';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Guide rings
    ([
      [0.25, 'rgba(255,255,255,.05)', [3, 5]],
      [0.50, 'rgba(151,184,203,.22)', []     ],
      [0.75, 'rgba(255,255,255,.05)', [3, 5]],
      [1.00, 'rgba(255,255,255,.06)', [2, 4]],
    ] as [number, string, number[]][]).forEach(([f, stroke, dash]) => {
      ctx.beginPath();
      ctx.arc(CX, CY, MAX_R * f, 0, Math.PI * 2);
      ctx.strokeStyle = stroke;
      ctx.lineWidth   = f === 0.5 ? 1 : 0.5;
      ctx.setLineDash(dash);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    const pts = embed.map((val, i) => {
      const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
      const r     = Math.max(2, BASE_R + val * BASE_R);
      const h     = ((i / N) * 360 + hueOffset) % 360;
      return {
        x:  CX + r * Math.cos(angle),
        y:  CY + r * Math.sin(angle),
        bx: CX + BASE_R * Math.cos(angle),
        by: CY + BASE_R * Math.sin(angle),
        col: `hsl(${h},100%,60%)`,
      };
    });

    // Spokes
    pts.forEach(p => {
      ctx.beginPath();
      ctx.moveTo(CX, CY);
      ctx.lineTo(p.bx, p.by);
      ctx.strokeStyle = p.col + '18';
      ctx.lineWidth   = 0.5;
      ctx.stroke();
    });

    // Filled polygon
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < N; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
    const grd = ctx.createRadialGradient(CX, CY, 0, CX, CY, MAX_R);
    grd.addColorStop(0,    'rgba(0,245,255,.16)');
    grd.addColorStop(0.45, 'rgba(140,0,255,.12)');
    grd.addColorStop(0.8,  'rgba(255,0,120,.07)');
    grd.addColorStop(1,    'rgba(0,0,0,0)');
    ctx.fillStyle = grd;
    ctx.fill();

    // Neon outline
    ctx.save();
    for (let i = 0; i < N; i++) {
      const next = pts[(i + 1) % N];
      ctx.beginPath();
      ctx.moveTo(pts[i].x, pts[i].y);
      ctx.lineTo(next.x, next.y);
      ctx.strokeStyle = pts[i].col;
      ctx.shadowColor = pts[i].col;
      ctx.shadowBlur  = 4;
      ctx.lineWidth   = 1.5;
      ctx.stroke();
    }
    ctx.restore();

    // Vertex dots
    ctx.save();
    pts.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
      ctx.fillStyle   = p.col;
      ctx.shadowColor = p.col;
      ctx.shadowBlur  = 5;
      ctx.fill();
    });
    ctx.restore();

    ctx.beginPath();
    ctx.arc(CX, CY, 2, 0, Math.PI * 2);
    ctx.fillStyle   = '#97B8CB';
    ctx.shadowColor = '#97B8CB';
    ctx.shadowBlur  = 7;
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  // ── Draw: signed difference ───────────────────────────────────────────────

  private drawDiff(scaled: number[], phase: number): void {
    const { ctx, CX, CY, MAX_R, BASE_R } = this;
    const N = 64;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = '#080d18';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Guide rings
    ([
      [0.5, 'rgba(151,184,203,.22)', [] as number[]],
      [1.0, 'rgba(255,255,255,.06)', [2, 4]],
    ] as [number, string, number[]][]).forEach(([f, stroke, dash]) => {
      ctx.beginPath();
      ctx.arc(CX, CY, MAX_R * f, 0, Math.PI * 2);
      ctx.strokeStyle = stroke;
      ctx.lineWidth = f === 0.5 ? 1 : 0.5;
      ctx.setLineDash(dash);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    const pts = scaled.map((val, i) => {
      const angle   = (i / N) * Math.PI * 2 - Math.PI / 2;
      const r       = Math.max(2, BASE_R + val * BASE_R);
      const baseHue = val >= 0 ? 42 : 185;
      const wobble  = Math.sin((phase / 360) * Math.PI * 2 + i * 0.3) * 12;
      return {
        x:   CX + r * Math.cos(angle),
        y:   CY + r * Math.sin(angle),
        bx:  CX + BASE_R * Math.cos(angle),
        by:  CY + BASE_R * Math.sin(angle),
        col: `hsl(${baseHue + wobble},100%,${55 + Math.abs(val) * 15}%)`,
      };
    });

    // Store for hover
    this.lastPts = pts.map(p => ({ x: p.x, y: p.y }));

    // Filled polygon
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < N; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
    const grd = ctx.createRadialGradient(CX, CY, 0, CX, CY, MAX_R);
    grd.addColorStop(0,   'rgba(212,179,70,.14)');
    grd.addColorStop(0.5, 'rgba(0,180,180,.08)');
    grd.addColorStop(1,   'rgba(0,0,0,0)');
    ctx.fillStyle = grd;
    ctx.fill();

    // Neon outline
    ctx.save();
    for (let i = 0; i < N; i++) {
      const next = pts[(i + 1) % N];
      ctx.beginPath();
      ctx.moveTo(pts[i].x, pts[i].y);
      ctx.lineTo(next.x, next.y);
      ctx.strokeStyle = pts[i].col;
      ctx.shadowColor = pts[i].col;
      ctx.shadowBlur  = 5;
      ctx.lineWidth   = 1.5;
      ctx.stroke();
    }
    ctx.restore();

    // Vertex dots — slightly larger for hover hit targets
    ctx.save();
    pts.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.4, 0, Math.PI * 2);
      ctx.fillStyle   = p.col;
      ctx.shadowColor = p.col;
      ctx.shadowBlur  = 6;
      ctx.fill();
    });
    ctx.restore();

    ctx.beginPath();
    ctx.arc(CX, CY, 2, 0, Math.PI * 2);
    ctx.fillStyle   = '#97B8CB';
    ctx.shadowColor = '#97B8CB';
    ctx.shadowBlur  = 7;
    ctx.fill();
    ctx.shadowBlur = 0;
  }
}
